import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { fileApi } from '../../services/api';

/**
 * PhotoThumb — renders a thumbnail with a click-to-open lightbox viewer.
 *
 * The lightbox:
 *  • Opens on thumbnail click
 *  • Closes only via the ✕ button or Escape key (not on mouse-out)
 *  • Supports zoom in / zoom out via toolbar buttons or scroll wheel
 *  • Panning when zoomed in (click-drag on the image)
 *
 * HOW IT WORKS
 * ─────────────
 * S3 bucket is private — GET /api/v1/files/{fileId}/content fetches bytes
 * server-side, base64-encodes them, and returns a JSON payload. The component
 * builds a data URI and passes it to <img>.
 *
 * PROPS
 * ─────
 * fileId   {number}  — id of an ArticleFile already saved on the server.
 * src      {string}  — a local object URL (URL.createObjectURL).
 * label    {string}  — caption shown in the lightbox footer.
 * small    {bool}    — renders at 48×48 instead of the default 112×112.
 * pending  {bool}    — adds a "PENDING" badge (used with src for local previews).
 */
// Module-level cache so the same photo is not re-downloaded (as a large base64 JSON payload) every time a page
// re-renders or refetches, e.g. after an approve/reject action. Concurrent requests share one in-flight promise.
const photoCache = new Map();
const loadPhoto = (fileId) => {
  if (!photoCache.has(fileId)) {
    const p = fileApi.getFileContent(fileId)
      .then(res => `data:${res.data.contentType};base64,${res.data.base64Data}`)
      .catch(err => { photoCache.delete(fileId); throw err; });
    photoCache.set(fileId, p);
  }
  return photoCache.get(fileId);
};

export default function PhotoThumb({ fileId, src, label, small = false, pending = false }) {
  const [open,    setOpen]    = useState(false);
  const [dataUri, setDataUri] = useState(src || null);
  const [loading, setLoading] = useState(!!fileId);
  const [errored, setErrored] = useState(false);

  // Zoom & pan state (reset each time the lightbox opens)
  const [zoom,    setZoom]    = useState(1);
  const [pan,     setPan]     = useState({ x: 0, y: 0 });
  const dragging              = useRef(false);
  const dragStart             = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  // Fetch from backend when fileId is provided
  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    setLoading(true);
    setErrored(false);

    loadPhoto(fileId)
      .then(uri => {
        if (cancelled) return;
        setDataUri(uri);
      })
      .catch(() => { if (!cancelled) setErrored(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fileId]);

  // Reset zoom/pan whenever the lightbox opens
  useEffect(() => {
    if (open) { setZoom(1); setPan({ x: 0, y: 0 }); }
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  // Scroll-wheel zoom on the lightbox image
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => {
      const delta = e.deltaY < 0 ? 0.15 : -0.15;
      return Math.min(5, Math.max(0.5, z + delta));
    });
  }, []);

  // Pan: mouse-down starts drag
  const handleMouseDown = useCallback((e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
  }, [zoom, pan]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    setPan({
      x: dragStart.current.px + (e.clientX - dragStart.current.mx),
      y: dragStart.current.py + (e.clientY - dragStart.current.my),
    });
  }, []);

  const handleMouseUp = useCallback(() => { dragging.current = false; }, []);

  const zoomIn  = () => setZoom(z => Math.min(5,   parseFloat((z + 0.25).toFixed(2))));
  const zoomOut = () => setZoom(z => Math.max(0.5,  parseFloat((z - 0.25).toFixed(2))));
  const zoomReset = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const size = small ? 'w-12 h-12' : 'w-28 h-28';

  // ── Loading placeholder ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`${size} rounded-xl border-2 border-gray-200 bg-gray-100 flex-shrink-0
                       flex items-center justify-center`}>
        <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg"
          fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
      </div>
    );
  }

  // ── Broken-image fallback ──────────────────────────────────────────────────
  if (errored || !dataUri) {
    return (
      <div className={`${size} rounded-xl border-2 border-dashed border-gray-200 bg-gray-50
                       flex-shrink-0 flex items-center justify-center`}
        title="Image unavailable">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
          className="h-5 w-5 text-gray-300">
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409
               a2.25 2.25 0 013.182 0l2.909 2.909M18 3H6a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V6a3 3 0 00-3-3zM9.75 9.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
        </svg>
      </div>
    );
  }

  // ── Thumbnail ──────────────────────────────────────────────────────────────
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        title="Click to view"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
        className={`relative ${size} rounded-xl overflow-hidden border-2 flex-shrink-0 cursor-zoom-in
                    group ${pending ? 'border-blue-400' : 'border-gray-200 hover:border-amber-400'}
                    transition-colors`}
      >
        <img src={dataUri} alt={label || 'photo'} className="w-full h-full object-cover" />

        {/* Hover overlay hint */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors
                        flex items-center justify-center opacity-0 group-hover:opacity-100">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2}
            strokeLinecap="round" className="h-6 w-6 drop-shadow">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="11" y1="8" x2="11" y2="14"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
        </div>

        {pending && (
          <div className="absolute bottom-0 left-0 right-0 bg-blue-500/80 text-white
                          text-center py-0.5" style={{ fontSize: '9px' }}>
            PENDING
          </div>
        )}
      </div>

      {/* ── Lightbox portal ─────────────────────────────────────────────────── */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex flex-col"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* ── Toolbar ─────────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-5 py-3 flex-shrink-0"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}>

            {/* Caption */}
            <span className="text-white/80 text-sm font-medium truncate max-w-xs">
              {label || 'Photo'}
            </span>

            {/* Zoom controls */}
            <div className="flex items-center gap-1">
              <button onClick={zoomOut} disabled={zoom <= 0.5}
                title="Zoom out"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80
                           hover:text-white hover:bg-white/15 disabled:opacity-30 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                  strokeLinecap="round" className="h-4 w-4">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
              </button>

              <button onClick={zoomReset}
                title="Reset zoom"
                className="min-w-[52px] h-8 px-2 rounded-lg text-white/80 text-xs font-mono
                           hover:text-white hover:bg-white/15 transition-colors tabular-nums">
                {Math.round(zoom * 100)}%
              </button>

              <button onClick={zoomIn} disabled={zoom >= 5}
                title="Zoom in"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80
                           hover:text-white hover:bg-white/15 disabled:opacity-30 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                  strokeLinecap="round" className="h-4 w-4">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  <line x1="11" y1="8" x2="11" y2="14"/>
                  <line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
              </button>

              {/* Divider */}
              <div className="w-px h-5 bg-white/20 mx-1"/>

              {/* Close button */}
              <button onClick={() => setOpen(false)}
                title="Close (Esc)"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80
                           hover:text-white hover:bg-red-500/70 transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}
                  strokeLinecap="round" className="h-4 w-4">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ── Image area ──────────────────────────────────────────────────── */}
          <div
            className="flex-1 overflow-hidden flex items-center justify-center"
            style={{ cursor: zoom > 1 ? (dragging.current ? 'grabbing' : 'grab') : 'default' }}
            onWheel={handleWheel}
          >
            <img
              src={dataUri}
              alt={label || 'photo'}
              draggable={false}
              onMouseDown={handleMouseDown}
              style={{
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                transformOrigin: 'center center',
                maxWidth:  '92vw',
                maxHeight: 'calc(100vh - 120px)',
                objectFit: 'contain',
                userSelect: 'none',
                transition: dragging.current ? 'none' : 'transform 0.15s ease',
                borderRadius: 8,
                boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
              }}
            />
          </div>

          {/* ── Footer hint ─────────────────────────────────────────────────── */}
          <div className="flex items-center justify-center gap-4 py-2.5 flex-shrink-0 text-white/35 text-xs">
            <span>Scroll to zoom</span>
            <span>·</span>
            <span>Drag to pan when zoomed</span>
            <span>·</span>
            <span>Esc to close</span>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
